import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { ArrowUp, ArrowDown, Minus, Clock } from 'lucide-react';

const fmtVal = (v, name) => {
  if (!v && v !== 0) return '--';
  // Yields and rates: show as percentage
  if (name?.includes('Treasury') || name?.includes('Fed Funds') || name?.includes('VIX'))
    return v.toFixed(2);
  if (v >= 10000) return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (v >= 100) return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v.toFixed(4);
};

function TickerItem({ name, value, change, changePct, direction, delayed, asOf, isTsp }) {
  const isUp = direction === 'up' || change > 0;
  const isDown = direction === 'down' || change < 0;
  const color = isUp ? 'text-emerald-500' : isDown ? 'text-red-500' : 'text-[#6B7280]';
  const DirIcon = isUp ? ArrowUp : isDown ? ArrowDown : Minus;

  return (
    <div
      className="inline-flex items-center gap-2 px-4 shrink-0 group relative"
      data-testid={`ticker-item-${name?.toLowerCase().replace(/[\s\/]+/g, '-')}`}
    >
      <span className="text-[11px] font-medium text-[#8A96A3] whitespace-nowrap">{name}</span>
      <span className="text-[11px] font-mono font-semibold text-[#1B2A4A] whitespace-nowrap">
        {isTsp ? '$' : ''}{fmtVal(value, name)}
        {name?.includes('Treasury') || name?.includes('Fed Funds') ? '%' : ''}
      </span>
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono font-medium whitespace-nowrap ${color}`}>
        <DirIcon size={9} strokeWidth={2.5} />
        {change !== undefined && change !== null ? (isUp ? '+' : '') + change.toFixed(2) : ''}
        {changePct !== undefined && changePct !== null ? ` (${isUp ? '+' : ''}${changePct.toFixed(2)}%)` : ''}
      </span>
      {delayed && (
        <span className="text-[9px] font-mono font-bold text-amber-500 tracking-wider" data-testid="ticker-delayed-label">DELAYED</span>
      )}
      {/* Tooltip for UniFed Core items */}
      {isTsp && asOf && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-50">
          <div className="bg-[#1B2A4A] text-white text-[10px] font-mono px-2 py-1 rounded whitespace-nowrap shadow-lg" data-testid="ticker-as-of-tooltip">
            <Clock size={9} className="inline mr-1" />As of {asOf}
          </div>
        </div>
      )}
      {/* Separator dot */}
      <span className="text-[#D1D5DB] text-[8px] ml-1">|</span>
    </div>
  );
}

export function MarketTicker() {
  const [indices, setIndices] = useState([]);
  const [tspFunds, setTspFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const animRef = useRef(null);
  const posRef = useRef(0);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 120000); // Refresh every 2 min
    return () => clearInterval(interval);
  }, []);

  // CSS animation for smooth scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || (indices.length === 0 && tspFunds.length === 0)) return;

    let pos = 0;
    const speed = 0.5; // px per frame

    const animate = () => {
      pos += speed;
      const contentWidth = el.scrollWidth / 2; // We duplicate items
      if (pos >= contentWidth) pos = 0;
      el.style.transform = `translateX(-${pos}px)`;
      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    // Pause on hover
    const parent = el.parentElement;
    const pause = () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
    const resume = () => { animRef.current = requestAnimationFrame(animate); };
    parent?.addEventListener('mouseenter', pause);
    parent?.addEventListener('mouseleave', resume);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      parent?.removeEventListener('mouseenter', pause);
      parent?.removeEventListener('mouseleave', resume);
    };
  }, [indices, tspFunds]);

  const loadData = async () => {
    try {
      const [idxRes, tspRes] = await Promise.allSettled([
        api.get('/market/indices'),
        api.get('/market/tsp-funds'),
      ]);
      if (idxRes.status === 'fulfilled') setIndices(Array.isArray(idxRes.value.data) ? idxRes.value.data : []);
      if (tspRes.status === 'fulfilled') setTspFunds(Array.isArray(tspRes.value.data) ? tspRes.value.data : []);
    } catch {} finally { setLoading(false); }
  };

  if (loading && indices.length === 0) {
    return (
      <div className="h-8 bg-white border-b border-[#E2E5E9] flex items-center px-4" data-testid="market-ticker-loading">
        <span className="text-[10px] text-[#9CA3AF] font-mono animate-pulse">Loading market data...</span>
      </div>
    );
  }

  // Build ordered item list: S&P 500, NASDAQ, DJIA, 10-Yr, Fed Funds, G/F/C/S/I, Gold, Oil, VIX, 2-Yr
  const idxMap = {};
  indices.forEach(i => { idxMap[i.name] = i; });
  const tspMap = {};
  tspFunds.forEach(f => { tspMap[f.fund] = f; });

  const order = [
    'S&P 500', 'NASDAQ', 'DJIA', '10-Yr Treasury', 'Fed Funds Rate',
    'A Fund', 'B Fund', 'C Fund', 'D Fund', 'E Fund',
    'Gold', 'Oil WTI', 'VIX', '2-Yr Treasury'
  ];

  const items = order.map(name => {
    const isTsp = name.endsWith('Fund');
    if (isTsp) {
      const f = tspMap[name];
      return f ? {
        name, value: f.price, change: f.dayChange, changePct: f.dayChangePct,
        direction: f.dayChange > 0 ? 'up' : f.dayChange < 0 ? 'down' : 'flat',
        delayed: f.delayed, asOf: f.asOf, isTsp: true
      } : { name, value: null, change: null, changePct: null, direction: 'flat', delayed: true, isTsp: true };
    }
    const m = idxMap[name];
    return m ? {
      name, value: m.value, change: m.change, changePct: m.changePct,
      direction: m.direction, delayed: m.delayed, isTsp: false
    } : { name, value: null, change: null, changePct: null, direction: 'flat', delayed: true, isTsp: false };
  });

  // Duplicate for seamless looping
  const doubled = [...items, ...items];

  return (
    <div className="h-8 bg-white border-b border-[#E2E5E9] overflow-hidden relative" data-testid="market-ticker">
      <div ref={scrollRef} className="flex items-center h-full whitespace-nowrap will-change-transform">
        {doubled.map((item, i) => (
          <TickerItem key={`${item.name}-${i}`} {...item} />
        ))}
      </div>
    </div>
  );
}
